import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { UserSettingsModels } from '@share-models';
import { api } from '../consts/api.const';
import {
  SHARE_USER_ACCOUNT_SETTINGS_CONFIG,
  ShareUserAccountSettingsConfig,
} from '../../share-user-account-settings.provider';

@Injectable({
  providedIn: 'root',
})
export class UserSettingsService {
  private config = inject<ShareUserAccountSettingsConfig>(
    SHARE_USER_ACCOUNT_SETTINGS_CONFIG,
  );
  private http = inject(HttpClient);

  public create(
    data: UserSettingsModels.UserSettingsDocument,
  ): Observable<UserSettingsModels.UserSettingsDocument> {
    return this.http.post<UserSettingsModels.UserSettingsDocument>(
      api.userSettings.crud(this.apiUrl),
      data,
    );
  }

  public me(
    lang?: string,
  ): Observable<UserSettingsModels.UserSettingsDocument> {
    const url = lang
      ? `${api.userSettings.me(this.apiUrl)}?lang=${lang}`
      : api.userSettings.me(this.apiUrl);
    return this.http.get<UserSettingsModels.UserSettingsDocument>(url);
  }

  public update(
    data: UserSettingsModels.UserSettingsDocument,
  ): Observable<UserSettingsModels.UserSettingsDocument> {
    return this.http.put<UserSettingsModels.UserSettingsDocument>(
      api.userSettings.crud(this.apiUrl),
      data,
    );
  }

  public verifyPhone(phone: string): Observable<{ _id?: string; id?: string }> {
    return this.http.post<{ _id?: string; id?: string }>(
      api.userSettings.verification(this.apiUrl),
      { phone },
    );
  }

  public sendCode(data: {
    confirmationCode: number;
    verificationId: string;
  }): Observable<unknown> {
    return this.http.put<unknown>(
      api.userSettings.verificationOne(this.apiUrl, data.verificationId),
      data,
    );
  }

  public setLang(data: {
    lang: string;
  }): Observable<UserSettingsModels.UserSettingsDocument> {
    return this.http.put<UserSettingsModels.UserSettingsDocument>(
      api.userSettings.setLanguage(this.apiUrl),
      data,
    );
  }

  public setTheme(
    theme: string,
  ): Observable<UserSettingsModels.UserSettingsDocument> {
    return this.http.put<UserSettingsModels.UserSettingsDocument>(
      api.userSettings.setTheme(this.apiUrl),
      { theme },
    );
  }

  public setWeeklySubscription(
    subForWeekly: boolean,
  ): Observable<UserSettingsModels.UserSettingsDocument> {
    return this.http.put<UserSettingsModels.UserSettingsDocument>(
      api.userSettings.setWeeklySubscription(this.apiUrl),
      {
        subForWeekly,
      },
    );
  }

  public setDevicePageView(data: {
    newDevicePageView: boolean;
  }): Observable<UserSettingsModels.UserSettingsDocument> {
    return this.http.put<UserSettingsModels.UserSettingsDocument>(
      api.userSettings.setDevicePageView(this.apiUrl),
      data,
    );
  }

  public setGlobalSearchSettings(
    globalSearchSettings: UserSettingsModels.GlobalSearchSettingsModel,
  ): Observable<UserSettingsModels.UserSettingsDocument> {
    return this.http.put<UserSettingsModels.UserSettingsDocument>(
      api.userSettings.setGlobalSearchSettings(this.apiUrl),
      {
        globalSearchSettings,
      },
    );
  }

  private get apiUrl(): string {
    return this.config.apiUrl.replace(/\/$/, '');
  }
}
